import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { HpRespCollectionsModuleItem } from './HpRespCollectionsModuleItem';
import { HpSharedModuleContainer } from '../HpSharedModuleContainer/HpSharedModuleContainer';
import { RenderIn, device } from '../../utils/RenderIn';

import styles from './HpRespCollectionsModule.scss';
import { HpSharedCarouselTracking } from '../HpSharedCarouselTracking/HpSharedCarouselTracking';
import {
    trackCollectionsImpression,
    trackCollectionsItemClick,
    trackArrowClick,
} from './helpers/CollectionsTracking';

function HpRespCollectionsModuleComponent({ homepage }) {
    if (!homepage.collectionsModule) {
        return null;
    }

    const { title, items } = homepage.collectionsModule;
    const dataTn = `${title.toLowerCase().replace(/\s+/g, '-')}-container`;

    return (
        <HpSharedModuleContainer dataTn={dataTn}>
            <HpSharedModuleContainer.SubHeader>{title}</HpSharedModuleContainer.SubHeader>
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {() => {
                    const itemsPerPage = 5;

                    return (
                        <HpSharedCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handleIndexChange }) => (
                                <Carousel
                                    slideCarouselItems
                                    classNames={{ wrapper: styles.wrapper }}
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index, isVisible }) => (
                                        <HpRespCollectionsModuleItem
                                            item={items[index]}
                                            isVisible={isVisible}
                                            onClick={() =>
                                                trackCollectionsItemClick({
                                                    item: items[index],
                                                    index,
                                                })
                                            }
                                        />
                                    )}
                                    onIndexChange={handleIndexChange}
                                    onLeftArrowClick={() => trackArrowClick({ type: 'previous' })}
                                    onRightArrowClick={() => trackArrowClick({ type: 'next' })}
                                />
                            )}
                        </HpSharedCarouselTracking>
                    );
                }}
            </RenderIn>
            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 3.5;
                    return (
                        <HpSharedCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handlePageChange }) => (
                                <Swiper
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={(currentPage, previousPage) => {
                                        trackArrowClick({
                                            type: previousPage > currentPage ? 'previous' : 'next',
                                        });
                                        handlePageChange(currentPage);
                                    }}
                                >
                                    {items.map((item, index) => (
                                        <HpRespCollectionsModuleItem
                                            key={item.cmsId}
                                            item={item}
                                            onClick={() =>
                                                trackCollectionsItemClick({ item, index })
                                            }
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpSharedCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpSharedModuleContainer>
    );
}

HpRespCollectionsModuleComponent.propTypes = {
    homepage: PropTypes.object.isRequired,
};

export const HpRespCollectionsModule = createFragmentContainer(HpRespCollectionsModuleComponent, {
    homepage: graphql`
        fragment HpRespCollectionsModule_homepage on Homepage {
            collectionsModule: module(type: "collections") {
                ... on CollectionsModule {
                    title
                    items {
                        cmsId
                        title
                        link
                        imageUrl
                    }
                }
            }
        }
    `,
});
